import React, { useState, useRef } from 'react';
import {
    Save as SaveIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    ContentCopy as CopyIcon,
    Print as PrintIcon,
    FilterList as FilterListIcon,
    GridView as GridViewIcon,
    FormatListBulleted as TableListIcon,
    TableChart as ExcelIcon,
    Article as WordIcon
} from '@mui/icons-material';
import {
    Box, Paper, Typography, Button, Alert, CircularProgress, Collapse,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    IconButton, Tooltip, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import blobticService from '../services/blobticService';
import { BlobticDataManager } from '../services/blobticDataManager';
import { blobticMessage } from '../components/BlobticMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { useDefaultViewMode, ViewMode } from '../contexts/ThemeContext';

export { useDefaultViewMode, type ViewMode } from '../contexts/ThemeContext';

export interface CrudConfig {
    entityKey: string;   // e.g. "Blobtic_Academy,academy_room"
    actionSuffix?: string; // e.g. "Room" -> InsertRoom
    primaryKey: string;  // e.g. "ARM_IDF"
}

interface GenericPageProps<T = any> {
    title: string;
    icon?: React.ReactNode;
    additionalActions?: React.ReactNode;
    footerInfo?: {
        lastUpdate?: string;
        userLib?: string;
    };
    children: React.ReactNode | ((props: { viewMode: ViewMode; setViewMode: (mode: ViewMode) => void }) => React.ReactNode);
    disableScroll?: boolean;

    // View Mode Props (Grid vs Table)
    viewMode?: ViewMode;
    onViewModeChange?: (mode: ViewMode) => void;
    viewModeEnabled?: boolean;
    onNew?: () => void;
    newButtonLabel?: string;

    // CRUD Props
    crudConfig?: CrudConfig;
    data?: T;
    /**
     * Called AFTER the server Insert/Update completes.
     * IndexedDB is already updated by GenericPage automatically.
     * Use this only if the page needs additional local processing (e.g. navigate away).
     * Most pages can simply use onClear instead.
     */
    onDataChange?: (data: T) => void;
    onValidate?: () => string | null;
    onClear?: () => void;
    footerEnabled?: boolean;

    // boubaker 23/08/2026 - Type 'print' : page d'édition (impression).
    // boubaker 25/08/2026 - Type 'indicateur' : filtres + zone résultats (sans impression).
    // `filters` = zone de filtres affichable/masquable (gain de place pour les
    // résultats) ; le bouton Imprimer n'imprime que #printable-generic.
    type?: 'crud' | 'print' | 'indicateur';
    filters?: React.ReactNode;
    filtersDefaultOpen?: boolean;
    printLabel?: string;
    onPrint?: () => void | Promise<void>;
    onExportExcel?: () => void | Promise<void>;
    excelLabel?: string;
    onExportWord?: () => void | Promise<void>;
    wordLabel?: string;
}

function GenericPage<T extends Record<string, any>>({
    title,
    icon,
    additionalActions,
    footerInfo,
    children,
    crudConfig,
    data,
    onDataChange,
    onValidate,
    onClear,
    footerEnabled = true,
    disableScroll = false,
    type = 'crud',
    filters,
    filtersDefaultOpen = true,
    printLabel,
    onPrint,
    onExportExcel,
    excelLabel,
    onExportWord,
    wordLabel,
    viewMode,
    onViewModeChange,
    viewModeEnabled,
    onNew,
    newButtonLabel
}: GenericPageProps<T>) {
    
    const { t } = useLanguage();
    const [internalViewMode, setInternalViewMode] = useDefaultViewMode();
    const currentViewMode = viewMode ?? internalViewMode;
    const handleViewModeChange = onViewModeChange ?? setInternalViewMode;

    // By default, show Grid/Table viewMode switcher automatically on any CRUD list page (type 'crud' without crudConfig)
    // ONLY if the page supports viewMode (viewMode/onViewModeChange prop or render-prop children) or viewModeEnabled is explicitly true.
    const isCrudList = type === 'crud' && !crudConfig;
    const hasViewModeSupport = viewMode !== undefined || onViewModeChange !== undefined || typeof children === 'function';
    const showViewModeSwitcher = viewModeEnabled !== undefined ? viewModeEnabled : (isCrudList && hasViewModeSupport);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        type: 'delete' | 'update' | 'insert-copy' | null;
    }>({ open: false, type: null });
    const [isMaximized, setIsMaximized] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    // boubaker 23/08/2026 - mode édition : masquage des filtres pour agrandir la zone résultats
    const [filtersOpen, setFiltersOpen] = useState(filtersDefaultOpen);
    const isPrintType = type === 'print';
    const isIndicateurType = type === 'indicateur';
    const hasFilters = isPrintType || isIndicateurType;

    // Impression : hook optionnel (chargement/contrôles) puis impression navigateur.
    // Le CSS @media print ci-dessous ne rend visible que #printable-generic.
    const handlePrint = async () => {
        if (onPrint) await onPrint();
        window.print();
    };

    // ── Mode detection ────────────────────────────────────────────────────────
    // If data has a valid primary key, we are in Edit mode (Update / Delete / Duplicate).
    // If data has no primary key (null, 0, undefined, empty), we are in New / Insert mode.
    const pkValue = crudConfig && data ? (data[crudConfig.primaryKey] ?? null) : null;
    const isEditMode = Boolean(pkValue !== null && pkValue !== undefined && pkValue !== '' && pkValue !== 0);

    /**
     * Extracts the IndexedDB object store name from entityKey.
     * e.g. 'Blobtic_Academy,academy_room' -> 'academy_room'
     */
    const getObjectName = (): string => {
        if (!crudConfig?.entityKey) return '';
        const parts = crudConfig.entityKey.split(',');
        return parts[parts.length - 1].trim().toLowerCase();
    };

    const handleToggleMaximize = () => setIsMaximized(prev => !prev);

    // ── Core save executor ────────────────────────────────────────────────────
    // forceInsert=true → strips PK and sends Insert (used for "Duplicate" in edit mode)
    const executeSave = async (forceInsert = false) => {
        setLoading(true);
        setError(null);

        try {
            const pk = crudConfig!.primaryKey;
            const suffix = crudConfig!.actionSuffix || '';
            const isInsert = forceInsert || !isEditMode;
            const actionType = isInsert ? `Insert${suffix}` : `Update${suffix}`;

            // Insert: strip PK so server generates a new one.
            // Update: keep PK so server knows which row to update.
            const payload = isInsert
                ? (() => { const { [pk]: _stripped, ...rest } = (data as any); return rest; })()
                : { ...data };

            const response = await blobticService.executeZoomRequest({
                ZoomKey: 'CrudOperation',
                Action: { OKey: `${crudConfig!.entityKey},${actionType}`, ...payload }
            });

            if (response.success) {
                blobticMessage.success(
                    t('common.messages.success'),
                    isInsert ? t('common.messages.created') : t('common.messages.saved')
                );

                let responseData = response.data;
                if (Array.isArray(responseData) && responseData.length > 0) {
                    responseData = responseData[0];
                }

                // ── Generic IndexedDB update ─ no page code needed ─────────────────────
                const objectName = getObjectName();
                if (objectName && responseData) {
                    // Fire-and-forget: updates IndexedDB memory cache + notifies subscribers
                    BlobticDataManager.upsertItem(objectName, responseData);
                }

                if (responseData && onDataChange) {
                    onDataChange({ ...data, ...responseData } as T);
                } else if (onClear) {
                    onClear();
                }
            } else {
                setError(response.message || t('common.messages.save_error'));
            }
        } catch (err: any) {
            setError(err.message || t('common.messages.comm_error'));
        } finally {
            setLoading(false);
        }
    };

    // ── Save / Update button handler ──────────────────────────────────────────
    const handleSave = async () => {
        if (!crudConfig || !data) return;
        if (onValidate) {
            const err = onValidate();
            if (err) { setError(err); return; }
        }
        if (isEditMode) {
            setConfirmDialog({ open: true, type: 'update' });
        } else {
            await executeSave(false);
        }
    };

    // ── Insert-as-copy button handler (edit mode only) ────────────────────────
    const handleInsertCopy = () => {
        if (!crudConfig || !data) return;
        if (onValidate) {
            const err = onValidate();
            if (err) { setError(err); return; }
        }
        setConfirmDialog({ open: true, type: 'insert-copy' });
    };

    // ── Delete handler ────────────────────────────────────────────────────────
    const executeDelete = async () => {
        setLoading(true);
        try {
            const pk = crudConfig!.primaryKey;
            const suffix = crudConfig!.actionSuffix || '';
            const response = await blobticService.executeZoomRequest({
                ZoomKey: 'CrudOperation',
                Action: { OKey: `${crudConfig!.entityKey},Delete${suffix}`, [pk]: data![pk] }
            });
            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));

                // ── Generic IndexedDB remove ─ no page code needed ────────────────────
                const objectName = getObjectName();
                if (objectName) {
                    // Pass full data object so getEntityId can resolve the correct id
                    // (handles cases where IndexedDB keyPath ('id') differs from DB column (ARM_IDF))
                    BlobticDataManager.removeItem(objectName, data);
                }

                if (onClear) onClear();
            } else {
                setError(response.message || t('common.messages.delete_error'));
            }
        } catch (err: any) {
            setError(err.message || t('common.messages.comm_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        if (!crudConfig || !data || !data[crudConfig.primaryKey]) return;
        setConfirmDialog({ open: true, type: 'delete' });
    };

    const handleConfirmAction = async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        if (confirmDialog.type === 'delete') await executeDelete();
        else if (confirmDialog.type === 'update') await executeSave(false);
        else if (confirmDialog.type === 'insert-copy') await executeSave(true);
    };

    // ── Toolbar buttons ───────────────────────────────────────────────────────
    // New mode:  [Insert]
    // Edit mode: [Insert copy] [Delete] [Update]
    const crudActions = crudConfig && (
        <>
            {isEditMode && (
                <Tooltip title={t('common.actions.insert_copy')}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<CopyIcon />}
                        onClick={handleInsertCopy}
                        disabled={loading}
                    >
                        {t('common.actions.duplicate')}
                    </Button>
                </Tooltip>
            )}

            {isEditMode && (
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    disabled={loading}
                >
                    {t('common.actions.delete')}
                </Button>
            )}

            <Button
                variant="contained"
                startIcon={loading ? undefined : (isEditMode ? <SaveIcon /> : <AddIcon />)}
                onClick={handleSave}
                disabled={loading}
                sx={{ bgcolor: '#1a237e' }}
            >
                {loading
                    ? <CircularProgress size={24} color="inherit" />
                    : isEditMode
                        ? t('common.actions.update')   // 'Mettre à jour' / 'Update' / 'تحديث'
                        : t('common.actions.insert')   // 'Insérer' / 'Insert' / 'إدراج'
                }
            </Button>
        </>
    );

    return (
        <Box
            ref={rootRef}
            sx={{
                height: isMaximized ? '100vh' : '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default',
                position: isMaximized ? 'fixed' : 'relative',
                top: 0, left: 0,
                width: isMaximized ? '100vw' : '100%',
                zIndex: isMaximized ? 1300 : 'auto',
            }}
            className="generic-page-root"
        >
            <Paper
                elevation={1}
                className="no-print"
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 0,
                    zIndex: 10,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid rgba(0,0,0,0.1)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {icon && (
                        <Box sx={{ color: 'primary.main', display: 'flex' }}>
                            {React.cloneElement(icon as React.ReactElement, { fontSize: 'large' })}
                        </Box>
                    )}
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
                        {title}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex', gap: 1, alignItems: 'center',
                    '& .MuiButton-root': {
                        minWidth: { xs: '36px', sm: '64px' },
                        px: { xs: 1, sm: 2 },
                        fontSize: { xs: 0, sm: '0.875rem' },
                        '& .MuiButton-startIcon': {
                            m: 0,
                            marginInlineEnd: { xs: 0, sm: '8px' },
                            marginInlineStart: { xs: 0, sm: '-4px' },
                            '& > *:nth-of-type(1)': { fontSize: '1.25rem' }
                        },
                        '& > span:not(.MuiButton-startIcon):not(.MuiTouchRipple-root)': {
                            display: { xs: 'none', sm: 'inline' }
                        }
                    }
                }}>
                    {/* boubaker 25/08/2026 - Filtres toggle : print + indicateur */}
                    {hasFilters && (
                        <>
                            <Tooltip title={t('common.actions.filters')}>
                                <IconButton onClick={() => setFiltersOpen(prev => !prev)} color={filtersOpen ? 'primary' : 'default'}>
                                    <FilterListIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {isPrintType && (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                            >
                                {printLabel || t('common.actions.print')}
                            </Button>
                            {onExportExcel && (
                                <Button
                                    variant="outlined"
                                    color="success"
                                    startIcon={<ExcelIcon />}
                                    onClick={onExportExcel}
                                >
                                    {excelLabel || t('common.actions.excel') || 'Excel'}
                                </Button>
                            )}
                            {onExportWord && (
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<WordIcon />}
                                    onClick={onExportWord}
                                >
                                    {wordLabel || t('common.actions.word') || 'Word'}
                                </Button>
                            )}
                        </>
                    )}
                    <Tooltip title={isMaximized ? (t('common.actions.exitFullScreen') || 'Exit Full Screen') : (t('common.actions.enterFullScreen') || 'Full Screen')}>
                        <IconButton onClick={handleToggleMaximize} color="primary">
                            {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Tooltip>
                    {showViewModeSwitcher && (
                        <ToggleButtonGroup
                            value={currentViewMode}
                            exclusive
                            onChange={(_, val: ViewMode) => val && handleViewModeChange(val)}
                            size="small"
                            sx={{
                                bgcolor: 'background.paper',
                                '& .MuiToggleButton-root': {
                                    px: 1.5,
                                    py: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            bgcolor: 'primary.dark',
                                        }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="grid">
                                <Tooltip title={t('common.view_grid') || 'Vue Grille'}>
                                    <GridViewIcon fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="table">
                                <Tooltip title={t('common.view_table') || 'Vue Tableau'}>
                                    <TableListIcon fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {onNew && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={onNew}
                        >
                            {newButtonLabel || t('common.actions.new') || 'Nouveau'}
                        </Button>
                    )}
                    {additionalActions}
                    {crudActions}
                </Box>
            </Paper>

            {/* boubaker 23/08/2026 - zone filtres masquable (mode print/indicateur) */}
            {hasFilters && filters && (
                <Collapse in={filtersOpen} className="no-print">
                    <Box className="no-print" sx={{ p: 2, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                        {filters}
                    </Box>
                </Collapse>
            )}

            <Box className="generic-page-scroll-container" sx={{ flexGrow: 1, overflowY: disableScroll ? 'hidden' : 'auto', py: 0, px: 0, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', minHeight: 0, height: disableScroll ? '100%' : 'auto' }}>
                <Box className="generic-page-inner-content" sx={{ p: disableScroll ? 1 : 3, bgcolor: 'background.paper', height: disableScroll ? '100%' : 'auto', minHeight: disableScroll ? '100%' : '100%', display: 'flex', flexDirection: 'column' }}>
                    {error && <Alert severity="error" className="no-print" sx={{ mb: 2 }}>{error}</Alert>}
                    {isPrintType ? (
                        <>
                            {/* CSS impression : seul le contenu de la page d'édition est imprimé */}
                            <style>{`
                                @media print {
                                    /* Impression pleine page : le shell (sidebar MUI Drawer, footer) est RETIRÉ du flux */
                                    html, body {
                                        margin: 0 !important;
                                        padding: 0 !important;
                                        background: #fff !important;
                                        -webkit-print-color-adjust: exact !important;
                                        print-color-adjust: exact !important;
                                        color-adjust: exact !important;
                                    }
                                    * {
                                        -webkit-print-color-adjust: exact !important;
                                        print-color-adjust: exact !important;
                                        color-adjust: exact !important;
                                    }
                                    .no-print,
                                    .MuiDrawer-root,
                                    .MuiDrawer-root *,
                                    .MuiAppBar-root,
                                    .MuiAppBar-root *,
                                    header,
                                    footer,
                                    nav {
                                        display: none !important;
                                        height: 0 !important;
                                        max-height: 0 !important;
                                        margin: 0 !important;
                                        padding: 0 !important;
                                    }
                                    /* Libérer conteneurs de l'app-shell pour paginer sur plusieurs pages */
                                    html, body, #root, #root > div, main, .generic-page-root, .generic-page-scroll-container, .generic-page-inner-content {
                                        overflow: visible !important;
                                        height: auto !important;
                                        max-height: none !important;
                                        min-height: 0 !important;
                                        margin: 0 !important;
                                        padding: 0 !important;
                                        position: static !important;
                                        display: block !important;
                                    }
                                    #printable-generic {
                                        position: static !important;
                                        width: 100% !important;
                                        margin: 0 !important;
                                        padding: 0 !important;
                                        box-sizing: border-box !important;
                                        display: block !important;
                                        visibility: visible !important;
                                    }
                                    #printable-generic * {
                                        visibility: visible !important;
                                    }
                                }
                            `}</style>
                            <div id="printable-generic">
                                {typeof children === 'function' ? children({ viewMode: currentViewMode, setViewMode: handleViewModeChange }) : children}
                            </div>
                        </>
                    ) : (
                        typeof children === 'function' ? children({ viewMode: currentViewMode, setViewMode: handleViewModeChange }) : children
                    )}
                </Box>
            </Box>

            {footerEnabled && (
                <Box sx={{ p: 1.5, px: 3, bgcolor: 'background.paper', color: 'text.secondary', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem', borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                    <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('common.labels.lastUpdate')}</Typography>
                        <Typography variant="body2" component="span" sx={{ ml: 1, fontWeight: 500 }}>{footerInfo?.lastUpdate || '-'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('common.labels.user')}</Typography>
                        <Typography variant="body2" component="span" sx={{ ml: 1, fontWeight: 500 }}>{footerInfo?.userLib || '-'}</Typography>
                    </Box>
                </Box>
            )}

            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))} aria-labelledby="alert-dialog-title">
                <DialogTitle id="alert-dialog-title">
                    {confirmDialog.type === 'delete'
                        ? t('common.confirm.title.delete')
                        : confirmDialog.type === 'insert-copy'
                            ? t('common.confirm.title.insert') || t('common.confirm.title.update')
                            : t('common.confirm.title.update')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.type === 'delete'
                            ? t('common.confirm.delete')
                            : confirmDialog.type === 'insert-copy'
                                ? t('common.confirm.insert') || t('common.confirm.update')
                                : t('common.confirm.update')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>{t('btn.cancel')}</Button>
                    <Button onClick={handleConfirmAction} autoFocus color={confirmDialog.type === 'delete' ? 'error' : 'primary'}>
                        {t('common.actions.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default GenericPage;
